import { Fragment, useMemo } from "react";
import { QueryStatus, useQuery } from "@tanstack/react-query";
import { Cell, Pie, PieChart } from "recharts";
import { Eye } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { cn, message } from "@/lib/utils";
import { chartColors } from "@/lib/chart";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";
import { ShareOfVoiceAPIData } from "@/schema/brand-os/brand-intel";

export type ShareOfVoiceParams = {
  highlightBrand: string;
};

function chartDataConfig(data: ShareOfVoiceAPIData) {
  const pieData = data.entries.map((entry, index) => ({
    name: entry.brand_name,
    value: entry.relative_value,
    fill: chartColors[index % chartColors.length],
  }));
  const chartConfig = data.entries.reduce(
    (acc, entry, index) => ({
      ...acc,
      [entry.brand_name]: { label: entry.brand_name, color: chartColors[index % chartColors.length] },
    }),
    {} as ChartConfig
  );
  return { pieData, chartConfig };
}

function ShareOfVoiceComponent({ status, voiceData, highlightBrand }: { status: QueryStatus; voiceData?: ShareOfVoiceAPIData | null; highlightBrand: string }) {
  switch (status) {
    case "pending":
      return <ShareOfVoiceSkeleton />;
    case "error":
      return <LockedContent message={message} />;
    case "success":
      if (!voiceData?.entries?.length) {
        return <LockedContent message={message} className="my-8" />;
      }
      return <ShareOfVoiceContent data={voiceData} highlightBrand={highlightBrand} />;
  }
}

export function ShareOfVoice({ params }: { params: ShareOfVoiceParams }) {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchShareOfVoice());

  return (
    <Card className="gap-3">
      <CardHeader className="flex items-center gap-2 pb-2">
        <div className="flex size-8 items-center justify-center rounded-lg bg-muted">
          <Eye className="size-4 text-muted-foreground" />
        </div>
        <CardTitle>Share of Voice</CardTitle>
      </CardHeader>
      <CardContent>
        <ShareOfVoiceComponent status={status} voiceData={data} highlightBrand={params.highlightBrand} />
      </CardContent>
    </Card>
  );
}

function ShareOfVoiceContent({ data, highlightBrand }: { data: ShareOfVoiceAPIData; highlightBrand: string }) {
  const { pieData, chartConfig } = useMemo(() => chartDataConfig(data), [data]);

  return (
    <Fragment>
      <ChartContainer config={chartConfig} className="mx-auto aspect-square w-full max-h-52 shrink-0">
        <PieChart accessibilityLayer>
          <ChartTooltip cursor={false} content={<ChartTooltipContent className="font-semibold" />} />
          <Pie data={pieData} dataKey="value" nameKey="name" innerRadius="60%" outerRadius="85%" label={false} strokeWidth={2} stroke="hsl(var(--background))">
            {pieData.map((entry) => (
              <Cell key={entry.name} fill={entry.fill} />
            ))}
          </Pie>
        </PieChart>
      </ChartContainer>
      <ul className="mt-auto flex flex-wrap justify-center gap-x-6 gap-y-2 pt-4">
        {data.entries.map((item, i) => {
          const isHighlighted = highlightBrand ? item.brand_name.toLowerCase() === highlightBrand.toLowerCase() : false;
          return (
            <li key={item.brand_name} className="flex items-center gap-2">
              <div className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: chartColors[i % chartColors.length] }} aria-hidden />
              <p className={cn("text-sm", isHighlighted ? "font-semibold text-foreground" : "text-foreground")}>{item.brand_name}</p>
              <p className={cn("text-sm tabular-nums", isHighlighted ? "font-semibold text-foreground" : "text-muted-foreground")}>{item.relative_value}%</p>
            </li>
          );
        })}
      </ul>
    </Fragment>
  );
}

function ShareOfVoiceSkeleton() {
  return (
    <Fragment>
      <Skeleton className="mx-auto aspect-square max-h-52 w-full max-w-48 rounded-full" />
      <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-5 w-28" />
        ))}
      </div>
    </Fragment>
  );
}
